import React, { useState } from 'react';
import {
  Button,
  ButtonProps,
  Icon,
  IconProps,
  Modal,
  TransitionablePortal,
} from 'semantic-ui-react';
import { Typography } from 'src/components/coreUI';
import { useResponsive } from 'ahooks';

export type Props = {
  title?: React.ReactNode;
  ariaLabel?: string;
  content?: React.ReactNode;
  onCancel?: () => void;
  onOk?: () => void | Promise<void>;
  cancelText?: string | null;
  okText?: string | null;
  isOpen?: boolean;
  onOpen?: () => void;
  iconProps?: IconProps;
  okButtonProps?: ButtonProps;
  cancelButtonProps?: ButtonProps;
  revert?: boolean;
};

const Confirm: React.FC<Props> = ({
  title,
  ariaLabel,
  okText,
  cancelText,
  onCancel,
  content,
  onOk,
  isOpen,
  iconProps,
  okButtonProps,
  cancelButtonProps,
  revert,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const screen = useResponsive();

  return (
    <TransitionablePortal
      open={isOpen}
      transition={{ animation: 'scale', duration: 200 }}
    >
      <Modal
        role="dialog"
        aria-label={
          ariaLabel || (typeof title === 'string' ? title : undefined)
        }
        open
        closeIcon
        closeOnEscape
        closeOnDimmerClick
        onClose={onCancel}
        size="tiny"
      >
        <Modal.Content>
          <Typography.Paragraph className="fw-bold">
            <Icon name="remove circle" color="red" {...iconProps} /> {title}
          </Typography.Paragraph>

          <div className="mb-24">{content}</div>

          <div
            className={`d-flex justify-end align-center ${
              revert ? 'flex-revert' : ''
            } ${!screen.md ? 'flex-col gap-16' : ''}`}
          >
            {!cancelButtonProps && cancelText !== null && (
              <a onClick={onCancel} className="px-24 cursor-pointer">
                {cancelText || 'Cancel'}
              </a>
            )}
            {okText !== null && (
              <Button
                loading={loading}
                onClick={async () => {
                  setLoading(true);
                  await onOk?.();
                  setLoading(false);
                }}
                primary
                {...okButtonProps}
              >
                {okText || 'OK'}
              </Button>
            )}
            {cancelButtonProps && (
              <Button
                onClick={() => {
                  onCancel?.();
                }}
                primary
                {...cancelButtonProps}
              >
                {cancelText || 'Cancel'}
              </Button>
            )}
          </div>
        </Modal.Content>
      </Modal>
    </TransitionablePortal>
  );
};

export default Confirm;
